import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft, Lock } from 'lucide-react';
import './MerchantAuth.css';

export default function PasswordReset() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Password validation states
  const [passwordValidations, setPasswordValidations] = useState({
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  });

  const validatePassword = (pwd) => {
    setPasswordValidations({
      minLength: pwd.length >= 8,
      hasUpperCase: /[A-Z]/.test(pwd),
      hasLowerCase: /[a-z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
    });
  };

  const isPasswordStrong = Object.values(passwordValidations).every(Boolean);

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing reset token. Please request a new password reset.');
    }
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'newPassword') {
      validatePassword(value);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setError('Invalid reset token');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!isPasswordStrong) {
      setError('Password does not meet security requirements');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || '/api'}/merchant/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token,
          newPassword: formData.newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Password reset successfully! You can now sign in with your new password.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        throw new Error(data.error || 'Failed to reset password');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="merchant-auth-container">
      {/* Left Side - Image with Text Overlay */}
      <div className="merchant-auth-left">
        <div className="merchant-auth-left-content">
          <div style={{ marginBottom: '2rem' }}>
            <Lock className="merchant-auth-left-icon" style={{ width: '4rem', height: '4rem', margin: '0 auto 1rem', color: '#a7f3d0' }} />
            <h1>Reset Your Password</h1>
            <p>
              Create a new secure password to regain access to your merchant account.
            </p>
          </div>
          
          <div className="merchant-auth-features">
            <div className="merchant-auth-feature">
              <CheckCircle className="merchant-auth-feature-icon" />
              <span className="merchant-auth-feature-text">Secure password reset process</span>
            </div>
            <div className="merchant-auth-feature">
              <CheckCircle className="merchant-auth-feature-icon" />
              <span className="merchant-auth-feature-text">Instant access to your dashboard</span>
            </div>
            <div className="merchant-auth-feature">
              <CheckCircle className="merchant-auth-feature-icon" />
              <span className="merchant-auth-feature-text">Enhanced account security</span>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Password Reset Form */}
      <div className="merchant-auth-right">
        <div className="merchant-auth-form-container">
          {/* Header */}
          <div className="merchant-auth-header">
            <h2 className="merchant-auth-title">Create New Password</h2>
            <p className="merchant-auth-subtitle">
              Enter a strong password to secure your account
            </p>
          </div>

          {/* Error/Success Messages */}
          {error && (
            <div className="merchant-auth-alert error">
              <AlertCircle className="merchant-auth-alert-icon" />
              <span className="merchant-auth-alert-text">{error}</span>
            </div>
          )}

          {success && (
            <div className="merchant-auth-alert success">
              <CheckCircle className="merchant-auth-alert-icon" />
              <span className="merchant-auth-alert-text">{success}</span>
            </div>
          )}

          {/* Password Reset Form */}
          <form onSubmit={handleSubmit} className="merchant-auth-form">
            <div className="merchant-auth-field">
              <label className="merchant-auth-label">
                New Password
              </label>
              <div className="merchant-auth-input-container">
                <Lock className="merchant-auth-input-icon" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  className="merchant-auth-input"
                  placeholder="Enter new password"
                  required
                  disabled={loading || !token}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="merchant-auth-input-toggle"
                >
                  {showPassword ? <EyeOff style={{ width: '1.25rem', height: '1.25rem' }} /> : <Eye style={{ width: '1.25rem', height: '1.25rem' }} />}
                </button>
              </div>
              {formData.newPassword && (
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <p style={{ display: 'flex', alignItems: 'center', color: passwordValidations.minLength ? '#10b981' : '#ef4444' }}>
                    {passwordValidations.minLength ? <CheckCircle size={16} style={{ marginRight: '0.25rem' }} /> : <AlertCircle size={16} style={{ marginRight: '0.25rem' }} />}
                    Min 8 characters
                  </p>
                  <p style={{ display: 'flex', alignItems: 'center', color: passwordValidations.hasUpperCase ? '#10b981' : '#ef4444' }}>
                    {passwordValidations.hasUpperCase ? <CheckCircle size={16} style={{ marginRight: '0.25rem' }} /> : <AlertCircle size={16} style={{ marginRight: '0.25rem' }} />}
                    Uppercase letter
                  </p>
                  <p style={{ display: 'flex', alignItems: 'center', color: passwordValidations.hasLowerCase ? '#10b981' : '#ef4444' }}>
                    {passwordValidations.hasLowerCase ? <CheckCircle size={16} style={{ marginRight: '0.25rem' }} /> : <AlertCircle size={16} style={{ marginRight: '0.25rem' }} />}
                    Lowercase letter
                  </p>
                  <p style={{ display: 'flex', alignItems: 'center', color: passwordValidations.hasNumber ? '#10b981' : '#ef4444' }}>
                    {passwordValidations.hasNumber ? <CheckCircle size={16} style={{ marginRight: '0.25rem' }} /> : <AlertCircle size={16} style={{ marginRight: '0.25rem' }} />}
                    Number
                  </p>
                  <p style={{ display: 'flex', alignItems: 'center', color: passwordValidations.hasSpecialChar ? '#10b981' : '#ef4444' }}>
                    {passwordValidations.hasSpecialChar ? <CheckCircle size={16} style={{ marginRight: '0.25rem' }} /> : <AlertCircle size={16} style={{ marginRight: '0.25rem' }} />}
                    Special character
                  </p>
                </div>
              )}
            </div>

            <div className="merchant-auth-field">
              <label className="merchant-auth-label">
                Confirm New Password
              </label>
              <div className="merchant-auth-input-container">
                <Lock className="merchant-auth-input-icon" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="merchant-auth-input"
                  placeholder="Confirm new password"
                  required
                  disabled={loading || !token}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="merchant-auth-input-toggle"
                >
                  {showConfirmPassword ? <EyeOff style={{ width: '1.25rem', height: '1.25rem' }} /> : <Eye style={{ width: '1.25rem', height: '1.25rem' }} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading || !token || !isPasswordStrong || formData.newPassword !== formData.confirmPassword}
              className="merchant-auth-button"
            >
              {loading ? (
                <>
                  <div className="merchant-auth-spinner" />
                  <span>Resetting Password...</span>
                </>
              ) : (
                <>
                  <span>Reset Password</span>
                  <CheckCircle style={{ width: '1.25rem', height: '1.25rem' }} />
                </>
              )}
            </button>
          </form>

          {/* Footer */}
          <div className="merchant-auth-footer">
            <p className="merchant-auth-footer-text">
              Remember your password?{' '}
              <button
                onClick={() => navigate('/login')}
                className="merchant-auth-footer-link"
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
